import { Injector } from '@angular/core';
import { BaseNode } from './BaseNode';
import { environment } from '../../../../environments/environment';
import { WebtraderService } from '../../service/webtrader-service/webtrader.service';
import { Art57Section } from '../Art57Section/Art57Section';
import { MedicinalproductsService } from '../../service/medicinalproducts-service/medicinalproducts.service';
import { Art57NodeUtils } from './Art57Node/Art57NodeUtils';
import { ResultList } from './ListEntity/ResultList';
import { SectionService } from '../../service/section-service/section-service';
import { ServiceEnum } from '../ServiceEnum';
import { QueryConditionsNode } from './QueryConditionsNode';
import { QueryFieldsNode } from './QueryFieldsNode';
import { ListColumn } from '../Data/ListColumn';
import { InBoxListView } from '../Data/WebTraderData/InBoxListView';
import { OutBoxListView } from '../Data/WebTraderData/OutboxListView';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DynamicListView } from '../Data/DynamicListView';
import { QueryResultNode } from './QueryResultNode';
import { TreeNode } from 'primeng/api';
import { MeddraService } from '../../service/meddra-service/meddra.service';
import { PaginationControl } from '../PaginationControl';
import { DateUtil } from '../DateUtil';
import { EntityEnum } from './EntityEnum';
import { ToEudravigilanceListView } from '../Data/WebTraderData/ToEudravigilanceListView';
import { FromEudravigilanceListView } from '../Data/WebTraderData/FromEudravigilanceListView';

/**
 * @param injector: Injector
 * @param data: Information retrieved from the backed normally represents the model
 * @param labelTemplate: Label Template for the node
 * @param fields: Formly fields used by the class, normaly comes from medicinal products file fieldDefinition-fieldsFormly
 * @param conditions: formly conditions used by the class, normaly comes from medicinal products file fieldDefinition-conditions, 
 * @param entityName: Represents the entity name to be used to call Backend endpoint service
 * @param serviceName: ServiceName Class from Front-End to be used when call to the backend services
 * @param entityNode: Entity node instance that represents the SipleSearchListNode that will be used to manage operations over class without search for it
 */
export class QueryNode extends BaseNode {
    service: any;
    entityName!: string;
    entityNode!: BaseNode | null;

    constructor(injector: Injector, data: any, labelTemplate: string, fields: FormlyFieldConfig[], conditions: FormlyFieldConfig[], entityName: string, serviceName: string, entityNode: BaseNode | null) {
        super(injector, data, labelTemplate);
        this.entityName = entityName;
        this.entityNode = entityNode;

        switch (serviceName) {
            case ServiceEnum.Meddra:
                this.service = this.ioc!.get(MeddraService);
                break;
            case ServiceEnum.Webtrader:
                this.service = this.ioc!.get(WebtraderService);
                break;
            case ServiceEnum.MedicinalProducts:
                this.service = this.ioc!.get(MedicinalproductsService);
                break;
        }

        this.children!.push(new QueryFieldsNode(injector, fields, {}));
        this.children!.push(new QueryConditionsNode(injector, conditions));
        this.children!.push(new ResultList(injector));

    }

    reset() {
        this.data.model = [];
        this.refreshLabel();
    }

    executeQuery(nodeRequest: any, newQuery: boolean = true) {
        const paginationControl = nodeRequest.paginationControl;
        if (paginationControl) {
            if (
                nodeRequest.constructor.name === '_QueryNodeInBox' ||
                nodeRequest.constructor.name === '_QueryNodeOutBox' ||
                (newQuery &&
                    nodeRequest.constructor.name !== 'WebTraderQueryResultNode')
            ) {
                nodeRequest.paginationControl = undefined;
            } else if (nodeRequest.constructor.name !== 'WebTraderQueryResultNode') {
                nodeRequest.paginationControl.moveTopPage(); // all queries returns to fisrt page
            }
        }
        this.getRecords(nodeRequest);
    }

    refreshQuery(nodeRequest: any) {
        if ((nodeRequest.constructor.name === '_QueryNodeInBox') ||
            (nodeRequest.constructor.name === '_QueryNodeOutBox') ||
            (nodeRequest.constructor.name === '_QueryNodeToEudravigilance') ||
            (nodeRequest.constructor.name === '_QueryNodeFromEudravigilance')) {
            nodeRequest.paginationControl = undefined;
        }
        this.getRecords(nodeRequest);
    }

    processResult(pageToRetrieve: PaginationControl, nodeRequest: BaseNode, data: any): void {
        let resultNodeList: TreeNode | null = Art57NodeUtils.findFirstChildNodeOfType(this, ResultList);
        let resultNode = null;
        if (resultNodeList) {


            if ((!(nodeRequest instanceof ResultList)) && (!(nodeRequest instanceof QueryResultNode))) {
                // This is the RUN scenario
                if (data && (data.length > 0)) {
                    const fieldsNode: QueryFieldsNode = (Art57NodeUtils.findFirstChildNodeOfType(this, QueryFieldsNode) as QueryFieldsNode);
                    const conditionsNode: QueryConditionsNode = (Art57NodeUtils.findFirstChildNodeOfType(this, QueryConditionsNode) as QueryConditionsNode);
                    const listViewInstance = new DynamicListView(data, fieldsNode.getFieldsToShow());
                    resultNode = new QueryResultNode(this.ioc!, listViewInstance, "Result " + DateUtil.getResultDateTime(), this.entityName, fieldsNode, conditionsNode, data);
                    resultNode.paginationControl = pageToRetrieve;
                    resultNode.parent = resultNodeList;
                    resultNode.lastData = data.model;
                    resultNodeList.children?.push(resultNode);
                    this.ioc!.get(SectionService).selectMedicinalProductsNode(resultNode);
                }
            } else {
                //This is the RERUN scenario
                nodeRequest.data.model = data;
                resultNode = nodeRequest;
                this.ioc!.get(SectionService).selectMedicinalProductsNode(resultNode);
            }

        }
    }


    getFields(): string {
        const fieldsNode: any = Art57NodeUtils.findFirstChildNodeOfType(this, QueryFieldsNode);
        if (fieldsNode == null) {
            return ''; // for WebTrader Inbox/Outbox has static fields (not send in webtrader servide)
        } else {
            return fieldsNode.getFieldsToRetrieve();
        }
    }

    getFieldsToShow(): ListColumn[] {
        const fieldsNode: any = Art57NodeUtils.findFirstChildNodeOfType(this, QueryFieldsNode);
        if (fieldsNode == null) {
            // compatibility with WebTrader Inbox/Outbox (hasn't got fields node because it needs to be hidden)
            if (this.constructor.name === '_QueryNodeInBox') {
                return (new InBoxListView({})).itemFinderArrayColumns;
            } else if (this.constructor.name === '_QueryNodeOutBox') {
                return (new OutBoxListView({})).itemFinderArrayColumns;
            } else if (this.constructor.name === '_QueryNodeToEudravigilance') {
                return (new ToEudravigilanceListView({})).itemFinderArrayColumns;
            } else if (this.constructor.name === '_QueryNodeFromEudravigilance') {
                return (new FromEudravigilanceListView({})).itemFinderArrayColumns;
            }

            return []; // it shouldn't happend
        } else {
            return fieldsNode.getFieldsToShow();
        }
    }

    getConditions(): string {
        let criteria = '';
        const conditionsNode: any = Art57NodeUtils.findFirstChildNodeOfType(this, QueryConditionsNode);
        if (conditionsNode) {
            criteria = conditionsNode.getCriteria();
        }
        return criteria;
    }

    getRecords(nodeRequest: any) {
        this.retrieveRecords(nodeRequest, false);
    }

    retrieveRecords(nodeRequest: any, retainData: boolean) {

        if (nodeRequest.paginationControl == null) {
            nodeRequest.lastData = undefined;
            const fieldsToRetrieve = this.getFields();
            let criteria = null;
            if (nodeRequest.constructor.name === 'WebtraderQueryResultNode') {
                criteria = nodeRequest.getConditions();
            } else {
                criteria = this.getConditions();
            }

            nodeRequest.paginationControl = new PaginationControl(); // creates te node if not exists
            nodeRequest.paginationControl.setFields(fieldsToRetrieve);
            nodeRequest.paginationControl.setCriteria(criteria);
        }


        if (this.ioc!.get(Art57Section).isWebtraderSection() && (this.ioc!.get(Art57Section).webtrader.selectedNode.constructor.name === '_QueryNodeInBox' ||
            this.ioc!.get(Art57Section).webtrader.selectedNode.constructor.name === '_QueryNodeOutBox' ||
            this.ioc!.get(Art57Section).webtrader.selectedNode.constructor.name === '_QueryNodeToEudravigilance' ||
            this.ioc?.get(Art57Section).webtrader.selectedNode.constructor.name === '_QueryNodeFromEudravigilance')) {
            nodeRequest.paginationControl.setCriteria('');
        }

        this.service.filter(nodeRequest, this.entityName, nodeRequest.paginationControl.criteria, nodeRequest.paginationControl.currentPage, false, nodeRequest.paginationControl.fields)
            .subscribe({
                next: (data: any) => {
                    if (data.result.length > 0) {
                        if (retainData) {
                            if (!nodeRequest.lastData) {
                                nodeRequest.lastData = [];
                            }
                            nodeRequest.lastData = [...nodeRequest.lastData, ...data.result];
                        } else {
                            nodeRequest.lastData = data.result;
                        }
                        this.processResult(nodeRequest.paginationControl, nodeRequest, nodeRequest.lastData);
                    } else {

                        if (nodeRequest.paginationControl.currentPage >= 1) {
                            nodeRequest.paginationControl.movePreviousPage(); // set the last page if moved to next, current allways have results if not first search and will be maintained as first page, previous never move before first page so allways exists result if come from previous page
                            if (nodeRequest.lastData  && nodeRequest.lastData.length > 0) {
                                this.processResult(nodeRequest.paginationControl, nodeRequest, nodeRequest.lastData);
                            }
                        } else {

                            this.processResult(nodeRequest.paginationControl, nodeRequest, []);
                        }
                        this.ioc!.get(Art57Section).showTooltipMessage({ severity: 'warn', detail: 'No result(s) found for the specified condition(s)', life: environment.warningElapsedTime });

                    }
                },
                error: (error: any) => {
                    this.ioc!.get(Art57Section).showTooltipMessage({ severity: 'error', detail: 'Error found', life: environment.errorElapsedTime });
                    throw error;
                }
            });

    }

    nextPage(nodeRequest: any) {
        nodeRequest.paginationControl.moveNextPage();
        this.getRecords(nodeRequest);
    }

    topPage(nodeRequest: any) {
        nodeRequest.paginationControl.moveTopPage();
        this.getRecords(nodeRequest);
    }
    previousPage(nodeRequest: any) {
        nodeRequest.paginationControl.movePreviousPage();
        this.getRecords(nodeRequest);
    }
    //US-328923: load more results besides the 50 loaded by default in the active area.
    addPage(nodeRequest: any) {
        nodeRequest.paginationControl.moveNextPage();
        this.retrieveRecords(nodeRequest, true);
    }

} 